/**
 * Cursor renderer.
 * Updates the position and appearance of cursor elements (crosshair, reticle, label).
 * Enhanced with HUD-style reticle brackets and twitch animation.
 */

import { DEFAULT_CHART_CONFIG } from '../config/constants';
import type { LayoutConfig, PriceConfig } from '../config/types';
import { DEFAULT_PRICE_CONFIG } from '../utils/priceFormat';

import type { SVGSelection, SelectionsCache } from './types';

// Reticle bracket dimensions for price label
const BRACKET_SIZE = 6;
const BRACKET_OFFSET = 4;

// Cursor reticle dimensions
const RETICLE_SIZE = 10; // Distance from center to bracket corner
const RETICLE_ARM_LENGTH = 5; // Length of bracket arms

/**
 * Twitch animation state for the cursor reticle.
 * Should be stored in a ref in the parent component to avoid module-level mutable state.
 */
export interface TwitchState {
    previousValue: number;
    scale: number;
    decay: number;
}

/**
 * Creates initial twitch state for cursor animation.
 */
export const createTwitchState = (initialValue: number = 0): TwitchState => ({
    previousValue: initialValue,
    scale: 1.0,
    decay: 0,
});

/**
 * Updates the position and color of the cursor elements.
 *
 * @param svg - D3 SVG selection
 * @param x - Cursor X position
 * @param y - Cursor Y position
 * @param value - Current price value
 * @param width - Chart width in pixels
 * @param height - Chart height in pixels
 * @param color - Current trend color
 * @param layout - Layout configuration (uses defaults if not provided)
 * @param priceConfig - Price formatting configuration (uses defaults if not provided)
 * @param twitchState - Mutable twitch animation state (stored in parent ref)
 * @param cache - Optional cached D3 selections (avoids DOM queries)
 */
export const renderCursor = (
    svg: SVGSelection,
    x: number,
    y: number,
    value: number,
    width: number,
    height: number,
    color: string,
    layout: LayoutConfig = DEFAULT_CHART_CONFIG.layout,
    priceConfig: PriceConfig = DEFAULT_PRICE_CONFIG,
    twitchState?: TwitchState,
    cache?: SelectionsCache,
): void => {
    const { cursorLabelOffsetX } = layout;

    // Compute twitch scale for cursor animation
    let currentTwitchScale = 1.0;

    if (twitchState) {
        // Detect price jumps for twitch animation (using scaled threshold)
        const priceDelta = Math.abs(value - twitchState.previousValue);
        if (priceDelta > priceConfig.twitchThreshold) {
            // Scale twitch relative to price magnitude
            const normalizedDelta =
                priceDelta / (priceConfig.twitchThreshold * 10);
            twitchState.scale = 1.0 + Math.min(normalizedDelta * 0.15, 0.3); // Scale up to 1.3x max
            twitchState.decay = 8; // Frames to decay
        }

        // Decay twitch back to 1.0
        if (twitchState.decay > 0) {
            twitchState.decay--;
            twitchState.scale = 1.0 + (twitchState.scale - 1.0) * 0.85; // Exponential decay
        } else {
            twitchState.scale = 1.0;
        }
        twitchState.previousValue = value;
        currentTwitchScale = twitchState.scale;
    }

    // Use cached selections if available, otherwise query DOM
    const reticle = cache?.cursorReticle ?? svg.select('.cursor-reticle');
    const cursorCore = cache?.cursorCore ?? reticle.select('.cursor-core');
    const bracketTl = cache?.bracketTl ?? reticle.select('.bracket-tl');
    const bracketTr = cache?.bracketTr ?? reticle.select('.bracket-tr');
    const bracketBl = cache?.bracketBl ?? reticle.select('.bracket-bl');
    const bracketBr = cache?.bracketBr ?? reticle.select('.bracket-br');
    const cursorLine = cache?.cursorLine ?? svg.select('.cursor-line');
    const cursorVertLine =
        cache?.cursorVertLine ?? svg.select('.cursor-vert-line');
    const labelGroup = cache?.cursorLabel ?? svg.select('.cursor-label');
    const labelRect = cache?.cursorLabelRect ?? labelGroup.select('rect');
    const labelText = cache?.cursorLabelText ?? labelGroup.select('text');

    // Cursor Reticle - bracket frame at the line tip
    reticle.attr(
        'transform',
        `translate(${x}, ${y}) scale(${currentTwitchScale})`,
    );

    // Update reticle core color
    cursorCore.attr('fill', 'white');

    // Update bracket paths with color - [ ] frame around the cursor point
    const s = RETICLE_SIZE;
    const a = RETICLE_ARM_LENGTH;

    // Top-left bracket corner
    bracketTl
        .attr('stroke', color)
        .attr('d', `M ${-s} ${-s + a} L ${-s} ${-s} L ${-s + a} ${-s}`);

    // Top-right bracket corner
    bracketTr
        .attr('stroke', color)
        .attr('d', `M ${s - a} ${-s} L ${s} ${-s} L ${s} ${-s + a}`);

    // Bottom-left bracket corner
    bracketBl
        .attr('stroke', color)
        .attr('d', `M ${-s} ${s - a} L ${-s} ${s} L ${-s + a} ${s}`);

    // Bottom-right bracket corner
    bracketBr
        .attr('stroke', color)
        .attr('d', `M ${s - a} ${s} L ${s} ${s} L ${s} ${s - a}`);

    // Crosshairs - horizontal line with varying opacity dash pattern
    cursorLine
        .attr('y1', y)
        .attr('y2', y)
        .attr('x2', width)
        .attr('stroke-dasharray', '8 4 2 4');

    cursorVertLine.attr('x1', x).attr('x2', x).attr('y2', height);

    // Label Bubble with reticle brackets
    const labelX = x + cursorLabelOffsetX;
    labelGroup.attr('transform', `translate(${labelX}, ${y})`);
    labelRect.attr('stroke', color);
    labelText.text(priceConfig.formatCursor(value)).attr('fill', color);

    // Update or create reticle brackets for the label
    const rectWidth = 64;
    const rectHeight = 24;
    const halfH = rectHeight / 2;

    // Check cache first, then query, then create if needed
    let reticleBrackets = cache?.reticleBrackets;
    let bracketLeft = cache?.bracketLeft;
    let bracketRight = cache?.bracketRight;
    let tickLeft = cache?.tickLeft;
    let tickRight = cache?.tickRight;

    // If not cached, try to select existing
    if (!reticleBrackets) {
        const existing = labelGroup.select<SVGGElement>('.reticle-brackets');
        if (!existing.empty()) {
            reticleBrackets = existing;
        }
    }

    // Create if doesn't exist
    if (!reticleBrackets || reticleBrackets.empty()) {
        reticleBrackets = labelGroup
            .append('g')
            .attr('class', 'reticle-brackets')
            .attr('opacity', 0.6);

        // Left bracket [ shape
        bracketLeft = reticleBrackets
            .append('path')
            .attr('class', 'bracket-left')
            .attr('fill', 'none')
            .attr('stroke-width', 1);

        // Right bracket ] shape
        bracketRight = reticleBrackets
            .append('path')
            .attr('class', 'bracket-right')
            .attr('fill', 'none')
            .attr('stroke-width', 1);

        // Left tick mark extending from label
        tickLeft = reticleBrackets
            .append('line')
            .attr('class', 'tick-left')
            .attr('stroke-width', 1)
            .attr('stroke-opacity', 0.4);

        // Right tick mark extending from label
        tickRight = reticleBrackets
            .append('line')
            .attr('class', 'tick-right')
            .attr('stroke-width', 1)
            .attr('stroke-opacity', 0.4);

        // Update cache if provided
        if (cache) {
            cache.reticleBrackets = reticleBrackets;
            cache.bracketLeft = bracketLeft;
            cache.bracketRight = bracketRight;
            cache.tickLeft = tickLeft;
            cache.tickRight = tickRight;
        }
    } else {
        // Use cached or select from group
        bracketLeft =
            bracketLeft ??
            reticleBrackets.select<SVGPathElement>('.bracket-left');
        bracketRight =
            bracketRight ??
            reticleBrackets.select<SVGPathElement>('.bracket-right');
        tickLeft =
            tickLeft ?? reticleBrackets.select<SVGLineElement>('.tick-left');
        tickRight =
            tickRight ?? reticleBrackets.select<SVGLineElement>('.tick-right');
    }

    // Update bracket paths
    // Left bracket [ - positioned to left of label
    bracketLeft?.attr('stroke', color).attr(
        'd',
        `
            M ${-BRACKET_OFFSET} ${-halfH - BRACKET_SIZE}
            L ${-BRACKET_OFFSET - BRACKET_SIZE} ${-halfH - BRACKET_SIZE}
            L ${-BRACKET_OFFSET - BRACKET_SIZE} ${halfH + BRACKET_SIZE}
            L ${-BRACKET_OFFSET} ${halfH + BRACKET_SIZE}
        `,
    );

    // Right bracket ] - positioned to right of label
    bracketRight?.attr('stroke', color).attr(
        'd',
        `
            M ${rectWidth + BRACKET_OFFSET} ${-halfH - BRACKET_SIZE}
            L ${rectWidth + BRACKET_OFFSET + BRACKET_SIZE} ${-halfH - BRACKET_SIZE}
            L ${rectWidth + BRACKET_OFFSET + BRACKET_SIZE} ${halfH + BRACKET_SIZE}
            L ${rectWidth + BRACKET_OFFSET} ${halfH + BRACKET_SIZE}
        `,
    );

    // Left tick - horizontal line extending left
    tickLeft
        ?.attr('stroke', color)
        .attr('x1', -BRACKET_OFFSET - BRACKET_SIZE - 4)
        .attr('y1', 0)
        .attr('x2', -BRACKET_OFFSET - BRACKET_SIZE - 12)
        .attr('y2', 0);

    // Right tick - horizontal line extending right
    tickRight
        ?.attr('stroke', color)
        .attr('x1', rectWidth + BRACKET_OFFSET + BRACKET_SIZE + 4)
        .attr('y1', 0)
        .attr('x2', rectWidth + BRACKET_OFFSET + BRACKET_SIZE + 12)
        .attr('y2', 0);
};
